import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Col,
  Empty,
  List,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvService, { type PmvControlTowerSnapshot } from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import {
  buildPmvProfileIdToLabelMap,
  buildProjectIdToLabelMap,
  resolvePmvProfileDisplayFromMap,
  resolveProjectDisplayFromMap,
} from '@/utils/pmvDisplay';
import {
  buildCommandCenterAttention,
  explainNullAvailability,
  explainNullUtilization,
  humanizeFuelExceptionReason,
  type PmvAttentionSeverity,
} from '@/utils/pmvUxCopy';

const { Text, Paragraph } = Typography;

function severityTag(severity: PmvAttentionSeverity) {
  if (severity === 'critical') return <Tag color="error">CRITICAL</Tag>;
  if (severity === 'warning') return <Tag color="warning">WARNING</Tag>;
  return <Tag>INFORMATION</Tag>;
}

const PmvControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const today = dayjs().format('YYYY-MM-DD');
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const profilesQ = useQuery({
    queryKey: ['pmv-profiles-labels'],
    queryFn: () => pmvService.listProfiles({ pageSize: 200 }),
    staleTime: 60_000,
  });
  const profileLabels = useMemo(
    () => buildPmvProfileIdToLabelMap(profilesQ.data?.data ?? []),
    [profilesQ.data?.data]
  );

  const towerQ = useQuery({
    queryKey: ['pmv-control-tower', today],
    queryFn: () => pmvService.getControlTower({ operating_date: today, publish_signals: false }),
    staleTime: 30_000,
  });

  const connectedQ = useQuery({
    queryKey: ['pmv-connected-summary'],
    queryFn: () => pmvService.getConnectedSummary(),
    staleTime: 60_000,
    retry: false,
  });
  const cs = connectedQ.data;

  const componentStripQ = useQuery({
    queryKey: ['pmv-component-attention-strip'],
    queryFn: () => pmvService.getComponentAttentionStrip(),
    staleTime: 60_000,
    retry: false,
  });
  const componentStrip = componentStripQ.data;

  const s = towerQ.data as PmvControlTowerSnapshot | undefined;
  const refreshedAt = s?.asOf ? dayjs(s.asOf).format('HH:mm:ss') : towerQ.dataUpdatedAt
    ? dayjs(towerQ.dataUpdatedAt).format('HH:mm:ss')
    : '—';

  const attentionItems = useMemo(() => {
    if (!s) return [];
    return buildCommandCenterAttention({
      missingLogs: s.daily?.missingLogs ?? 0,
      openBreakdowns: s.open_breakdowns ?? 0,
      breakdownUnits: s.breakdown ?? 0,
      pmDue: s.pm_due ?? 0,
      pmDue7d: s.pm_due_7d ?? 0,
      complianceExpiring30d: s.compliance_expiring_30d ?? 0,
      quarantined: s.quarantined ?? 0,
      fuelExceptions: s.fuel?.exceptions?.length ?? 0,
      backendAttention: s.attention ?? [],
    });
  }, [s]);

  const criticalCount = attentionItems.filter((i) => i.severity === 'critical').length;
  const missingLogs = s?.daily?.missingLogs ?? 0;

  const openFleet = (params?: Record<string, string>) => {
    const q = new URLSearchParams(params);
    const qs = q.toString();
    navigate(qs ? `/asset/pmv?${qs}` : '/asset/pmv');
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="PMV Command Center"
          documentNumber={s?.operatingDate ?? today}
          statusTag={<Tag color="geekblue">Plant & Fleet Operations</Tag>}
          subtitle={
            <Space wrap size="middle">
              <Text type="secondary">Operating date: {s?.operatingDate ?? today}</Text>
              <Text type="secondary">Last refreshed: {refreshedAt}</Text>
            </Space>
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/hub')}>Hub</Button>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Connected Fleet</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/map')}>Live Fleet Map</Button>
              <Button onClick={() => navigate('/asset/pmv/components/control-tower')}>
                Component Tower
              </Button>
              <Button onClick={() => navigate('/asset/pmv/operations')}>Deployment</Button>
              <Button type="primary" loading={towerQ.isFetching} onClick={() => void towerQ.refetch()}>
                Refresh
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {towerQ.isError && (
          <Alert
            type="error"
            showIcon
            message="Command Center could not load"
            description="Check PMV permissions and that PMV is enabled for this company."
            action={
              <Button size="small" onClick={() => void towerQ.refetch()}>
                Retry
              </Button>
            }
          />
        )}

        {(componentStrip?.headlines?.length ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Component attention"
            description={
              <Space direction="vertical" size={4}>
                {componentStrip!.headlines.map((line) => (
                  <Text key={line}>{line}</Text>
                ))}
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(componentStrip!.drill_href)}
                >
                  Open Component Control Tower
                </Button>
              </Space>
            }
          />
        )}

        {(s?.notes?.length ?? 0) > 0 && (
          <Alert
            type="info"
            showIcon
            message="Data completeness"
            description={s!.notes.join(' · ')}
          />
        )}

        <ControlTowerMetricStrip zone="metricsOrders" title="Fleet state">
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Fleet total"
              count={s?.fleet_total ?? 0}
              footerLink={{ label: 'Open fleet', onClick: () => openFleet() }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Available"
              count={s?.available ?? 0}
              footerLink={{ label: 'Filter', onClick: () => openFleet({ status: 'AVAILABLE' }) }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Allocated"
              count={s?.allocated ?? 0}
              footerLink={{ label: 'Deployment', onClick: () => navigate('/asset/pmv/operations') }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Deployed / operating"
              count={s?.demand?.deployed ?? s?.operating ?? 0}
              footerLink={{ label: 'Deployment', onClick: () => navigate('/asset/pmv/operations') }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="In transit"
              count={s?.demand?.inTransit ?? 0}
              urgency={(s?.demand?.inTransit ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{ label: 'Deployment', onClick: () => navigate('/asset/pmv/operations') }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Maintenance"
              count={s?.under_maintenance ?? 0}
              urgency={(s?.under_maintenance ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{ label: 'Open fleet', onClick: () => openFleet({ status: 'UNDER_MAINTENANCE' }) }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Breakdown"
              count={s?.breakdown ?? 0}
              urgency={(s?.breakdown ?? 0) > 0 ? 'critical' : 'neutral'}
              footerLink={{ label: 'Open fleet', onClick: () => openFleet({ status: 'BREAKDOWN' }) }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <ControlTowerMetricTile
              title="Compliance hold"
              count={s?.quarantined ?? 0}
              urgency={(s?.quarantined ?? 0) > 0 ? 'critical' : 'neutral'}
              footerLink={{ label: 'Open fleet', onClick: () => openFleet({ status: 'QUARANTINED' }) }}
            />
          </Col>
        </ControlTowerMetricStrip>

        {!connectedQ.isError && (
          <ControlTowerMetricStrip
            zone="metricsOrders"
            title="Connected fleet (second dimension — does not replace operational status)"
          >
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Connected units"
                count={cs?.connected_units ?? 0}
                footerLink={{ label: 'Overview', onClick: () => navigate('/asset/pmv/connected') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Live"
                count={cs?.online ?? 0}
                footerLink={{ label: 'Connected', onClick: () => navigate('/asset/pmv/connected') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Delayed"
                count={cs?.delayed ?? 0}
                urgency={(cs?.delayed ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'Connected', onClick: () => navigate('/asset/pmv/connected') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Offline"
                count={cs?.offline ?? 0}
                urgency={(cs?.offline ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'Connected', onClick: () => navigate('/asset/pmv/connected') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Unmapped devices"
                count={cs?.unmapped_devices ?? 0}
                urgency={(cs?.unmapped_devices ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Devices',
                  onClick: () => navigate('/asset/pmv/connected/devices?unassigned=1'),
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Reconciliation"
                count={cs?.open_reconciliation ?? 0}
                urgency={(cs?.open_reconciliation ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Review',
                  onClick: () => navigate('/asset/pmv/connected/reconciliation'),
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Quarantine / providers"
                count={(cs?.open_quarantine ?? 0) + (cs?.provider_issues ?? 0)}
                urgency={(cs?.open_quarantine ?? 0) + (cs?.provider_issues ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'Diagnostics', onClick: () => navigate('/asset/pmv/connected/diagnostics') }}
              />
            </Col>
          </ControlTowerMetricStrip>
        )}

        {!connectedQ.isError && cs?.spatial ? (
          <ControlTowerMetricStrip
            zone="metricsOrders"
            title="Spatial attention (evidence — does not mutate allocation or ops status)"
          >
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Location mismatches"
                count={cs.spatial.location_mismatches}
                urgency={cs.spatial.location_mismatches > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Review',
                  onClick: () => navigate('/asset/pmv/connected/movement-attention'),
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Restricted-area"
                count={cs.spatial.restricted_area}
                urgency={cs.spatial.restricted_area > 0 ? 'critical' : 'neutral'}
                footerLink={{
                  label: 'Review',
                  onClick: () => navigate('/asset/pmv/connected/movement-attention'),
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Unexpected movement"
                count={cs.spatial.unexpected_movement}
                urgency={cs.spatial.unexpected_movement > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Review',
                  onClick: () => navigate('/asset/pmv/connected/movement-attention'),
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Stale location"
                count={cs.spatial.stale_location}
                urgency={cs.spatial.stale_location > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: 'Fleet map', onClick: () => navigate('/asset/pmv/connected/map') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Live positions"
                count={cs.spatial.with_location}
                footerLink={{ label: 'Fleet map', onClick: () => navigate('/asset/pmv/connected/map') }}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Open spatial cases"
                count={cs.spatial.open_spatial_attention}
                urgency={cs.spatial.open_spatial_attention > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Attention',
                  onClick: () => navigate('/asset/pmv/connected/movement-attention'),
                }}
              />
            </Col>
          </ControlTowerMetricStrip>
        ) : null}

        <ControlTowerSectionCard
          zone={criticalCount > 0 ? 'overdue' : attentionItems.length > 0 ? 'attention' : 'neutral'}
          title={
            <Space>
              <span>Needs attention</span>
              <Tag>{attentionItems.length}</Tag>
            </Space>
          }
        >
          {attentionItems.length === 0 ? (
            <Text type="secondary">No attention items for this operating date.</Text>
          ) : (
            <List
              itemLayout="vertical"
              dataSource={attentionItems}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  actions={[
                    <Button key="go" type="link" onClick={() => navigate(item.href)}>
                      {item.actionLabel}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        {severityTag(item.severity)}
                        <Text strong>{item.title}</Text>
                        <Tag>{item.count}</Tag>
                      </Space>
                    }
                    description={item.explanation}
                  />
                </List.Item>
              )}
            />
          )}
        </ControlTowerSectionCard>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="metricsFulfilment" title="Utilization & availability">
              {s?.utilization_pct == null ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Utilization — data incomplete"
                  description={explainNullUtilization(missingLogs)}
                  action={
                    <Button size="small" onClick={() => navigate('/asset/pmv/daily-logs')}>
                      Review missing logs
                    </Button>
                  }
                />
              ) : (
                <ControlTowerMetricTile title="Utilization" count={s.utilization_pct} valueDisplay={`${s.utilization_pct}%`} />
              )}
              {s?.availability_pct == null ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                  message="Availability — data incomplete"
                  description={explainNullAvailability(missingLogs)}
                />
              ) : (
                <div style={{ marginTop: 12 }}>
                  <ControlTowerMetricTile
                    title="Availability"
                    count={s.availability_pct}
                    valueDisplay={`${s.availability_pct}%`}
                  />
                </div>
              )}
              <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                Working {s?.daily?.workingHours ?? 0} h · Idle {s?.daily?.idleHours ?? 0} h · Certified logs{' '}
                {s?.daily?.logsCertified ?? 0} / expected {s?.daily?.logsExpected ?? 0}
              </Paragraph>
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="documentPrep" title="Deployment intelligence">
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <ControlTowerMetricTile
                    title="Open requests"
                    count={s?.demand?.openRequests ?? 0}
                    footerLink={{ label: 'Requests', onClick: () => navigate('/asset/pmv/requests') }}
                  />
                </Col>
                <Col span={12}>
                  <ControlTowerMetricTile
                    title="Submitted"
                    count={s?.demand?.submittedRequests ?? 0}
                    urgency={(s?.demand?.submittedRequests ?? 0) > 0 ? 'attention' : 'neutral'}
                    footerLink={{ label: 'Requests', onClick: () => navigate('/asset/pmv/requests') }}
                  />
                </Col>
                <Col span={12}>
                  <ControlTowerMetricTile title="Reservations" count={s?.demand?.reservations ?? 0} />
                </Col>
                <Col span={12}>
                  <ControlTowerMetricTile
                    title="In transit"
                    count={s?.demand?.inTransit ?? 0}
                    footerLink={{ label: 'Deployment', onClick: () => navigate('/asset/pmv/operations') }}
                  />
                </Col>
              </Row>
            </ControlTowerSectionCard>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <ControlTowerSectionCard zone="dueToday" title="Reliability">
              <Space direction="vertical" style={{ width: '100%' }}>
                <ControlTowerMetricTile title="Open breakdowns" count={s?.open_breakdowns ?? 0} urgency={(s?.open_breakdowns ?? 0) > 0 ? 'critical' : 'neutral'} />
                <ControlTowerMetricTile title="PM overdue" count={s?.pm_due ?? 0} urgency={(s?.pm_due ?? 0) > 0 ? 'critical' : 'neutral'} />
                <ControlTowerMetricTile title="PM due 7d" count={s?.pm_due_7d ?? 0} urgency={(s?.pm_due_7d ?? 0) > 0 ? 'attention' : 'neutral'} />
                <ControlTowerMetricTile title="Under maintenance" count={s?.under_maintenance ?? 0} />
              </Space>
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} md={8}>
            <ControlTowerSectionCard zone="pendingApprovals" title="Compliance">
              <Space direction="vertical" style={{ width: '100%' }}>
                <ControlTowerMetricTile
                  title="Compliance hold"
                  count={s?.quarantined ?? 0}
                  urgency={(s?.quarantined ?? 0) > 0 ? 'critical' : 'neutral'}
                />
                <ControlTowerMetricTile
                  title="Insurance / compliance ≤30d"
                  count={s?.compliance_expiring_30d ?? 0}
                  urgency={(s?.compliance_expiring_30d ?? 0) > 0 ? 'attention' : 'neutral'}
                />
                <ControlTowerMetricTile title="Not ready" count={s?.not_ready ?? 0} />
                <ControlTowerMetricTile title="Out of service" count={s?.out_of_service ?? 0} />
              </Space>
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} md={8}>
            <ControlTowerSectionCard zone="metricsPayables" title="Fuel & operational cost">
              <Space direction="vertical" style={{ width: '100%' }}>
                <ControlTowerMetricTile
                  title="Fuel liters (30d)"
                  count={Math.round(s?.fuel?.totalLiters30d ?? 0)}
                  valueDisplay={(s?.fuel?.totalLiters30d ?? 0).toFixed(1)}
                />
                <ControlTowerMetricTile
                  title="Fuel exceptions"
                  count={s?.fuel?.exceptions?.length ?? 0}
                  urgency={(s?.fuel?.exceptions?.length ?? 0) > 0 ? 'attention' : 'neutral'}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Operational attribution only — PMV does not post GL.
                </Text>
              </Space>
            </ControlTowerSectionCard>
          </Col>
        </Row>

        <ControlTowerSectionCard zone="neutral" title="Fuel exceptions" sectionId="fuel-exceptions">
          {(s?.fuel?.exceptions?.length ?? 0) === 0 ? (
            <Text type="secondary">No fuel exceptions in the current window.</Text>
          ) : (
            <List
              size="small"
              dataSource={s!.fuel!.exceptions}
              renderItem={(e) => (
                <List.Item
                  actions={[
                    <Button key="u" type="link" onClick={() => navigate(`/asset/pmv/${e.pmv_profile_id}`)}>
                      Open equipment
                    </Button>,
                  ]}
                >
                  <Space direction="vertical" size={0}>
                    <Space wrap>
                      <Text strong>{resolvePmvProfileDisplayFromMap(profileLabels, e.pmv_profile_id)}</Text>
                      <Tag color="orange">{humanizeFuelExceptionReason(e.reason)}</Tag>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {e.detail}
                      <Text code style={{ marginLeft: 8, fontSize: 11 }}>
                        {e.reason}
                      </Text>
                    </Text>
                  </Space>
                </List.Item>
              )}
            />
          )}
        </ControlTowerSectionCard>

        <ControlTowerSectionCard zone="metricsFulfilment" title="Project view">
          {(s?.projectAllocation?.length ?? 0) === 0 ? (
            <Empty description="No active project allocations" />
          ) : (
            <Table
              size="small"
              rowKey="project_id"
              pagination={{ pageSize: 10 }}
              dataSource={s!.projectAllocation}
              onRow={(row) => ({
                style: { cursor: 'pointer' },
                onClick: () => navigate(`/prj/projects/${row.project_id}/pmv`),
              })}
              columns={[
                {
                  title: 'Project',
                  dataIndex: 'project_id',
                  ellipsis: true,
                  render: (id: string) => resolveProjectDisplayFromMap(projectLabels, id),
                },
                { title: 'Units deployed', dataIndex: 'allocated_count', width: 120 },
                {
                  title: 'Idle h',
                  dataIndex: 'idle_hours',
                  width: 90,
                  render: (v: number | null | undefined) =>
                    v == null ? (
                      <Text type="secondary" title="Certified log idle hours not available">
                        Unavailable
                      </Text>
                    ) : (
                      v
                    ),
                },
                {
                  title: 'Fuel L (30d)',
                  dataIndex: 'fuel_liters',
                  width: 110,
                  render: (v: number | null | undefined) => (v == null ? <Text type="secondary">Unavailable</Text> : v),
                },
                {
                  title: 'PMV cost',
                  dataIndex: 'operational_cost',
                  width: 110,
                  render: (v: number | null | undefined) =>
                    v == null ? <Text type="secondary">Unavailable</Text> : v.toFixed(2),
                },
              ]}
            />
          )}
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12, color: token.colorTextSecondary }}>
            Click a project to open the Project PMV workspace.
          </Text>
        </ControlTowerSectionCard>
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvControlTowerPage;
